import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { submitDealsLead } from '../actions/dealsLead';
import type { TDealsLeadFormResultModalStatus } from '../components/DealsLeadFormResultModal';
import { PHONE_MASK, PHONE_UNMASKED_LENGTH } from '../constants/dealsForm';
import { TThunkDispatch } from '../types/redux';
import {
  caClickSendCpd,
  caSendSuccessCpd,
  caShowCpdFail,
  type TCpdDealsLeadSource,
} from '../utils/analytics/cpdDeals/trackingCpdDeals';

interface IUseDealsLeadFormFieldsOptions {
  onAfterSubmit?: VoidFunction;
  source: TCpdDealsLeadSource;
  isTablet?: boolean;
}

const isPhoneFilled = (phone: string) => phone.replace(/\D/g, '').length === PHONE_UNMASKED_LENGTH;
const isRegionFilled = (regionId: string | number | null) => regionId != null && regionId !== '';

/**
 * Состояние и обработчики полей заявки продукта "Циан Сделки"
 * (номер телефона, регион, согласие) — общие для баннера и модалки
 */
export const useDealsLeadFormFields = ({ onAfterSubmit, source, isTablet }: IUseDealsLeadFormFieldsOptions) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const [phone, setPhone] = useState('');
  const [regionId, setRegionId] = useState<string | number | null>(null);
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [resultModalStatus, setResultModalStatus] = useState<TDealsLeadFormResultModalStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handlePhoneChange = useCallback((value: string) => {
    setPhone(value);
  }, []);

  const handleRegionChange = useCallback((value: string | number) => {
    setRegionId(value);
  }, []);

  const handleAgreementChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
    setIsAgreementChecked(value);
  }, []);

  const isSubmitDisabled = !isPhoneFilled(phone) || !isRegionFilled(regionId) || isSubmitted;

  const handleSubmit = useCallback(async () => {
    if (!isPhoneFilled(phone) || !isRegionFilled(regionId) || isSubmitted || isLoading) {
      return;
    }

    setIsLoading(true);
    caClickSendCpd({ source });

    try {
      const draftId = await dispatch(submitDealsLead({ phone, regionId }));

      onAfterSubmit?.();

      if (draftId) {
        caSendSuccessCpd({ source, draft_id: draftId });
        setIsSubmitted(true);
        setResultModalStatus('success');

        return;
      }

      caShowCpdFail({ source });
      setResultModalStatus('error');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, isSubmitted, isLoading, onAfterSubmit, phone, regionId, source]);

  const handleResultModalClose = useCallback(() => {
    setResultModalStatus(null);
  }, []);

  return {
    phoneProps: {
      size: 'M' as const,
      mask: PHONE_MASK,
      placeholder: 'Номер телефона',
      value: phone,
      onChange: handlePhoneChange,
    },
    selectProps: {
      size: 'M' as const,
      placeholder: isTablet ? 'Регион' : 'Выберите регион',
      value: regionId ?? undefined,
      onChange: handleRegionChange,
    },
    isAgreementChecked,
    resultModalProps: {
      status: resultModalStatus,
      onClose: handleResultModalClose,
    },
    submitButtonProps: {
      disabled: isSubmitDisabled,
      loading: isLoading,
      onClick: handleSubmit,
    },
    handleAgreementChange,
    handleSubmit,
  };
};
